import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useFormContext, useWatch } from 'react-hook-form';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import EarnIcon from '@flipster/react/icon/generated/EarnIcon';
import { css } from '@flipster/styled-system/css';
import { flex, vstack } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { earnSummaryBunja, getEarnSummaryOfCurrency } from '../../../../earn/state';
import { ConvertFormSchema } from '../../shared/validation';
import { convertModalBunja } from '../../state';

export const ConversionCompleteForm = () => {
  const { close } = useOverlayItem();
  const t = useTranslations();
  const { control } = useFormContext<ConvertFormSchema>();

  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { precisionAtom } = useBunja(convertModalBunja);
  const { fromCurrency, toCurrency, fromAmount, toAmount } = useWatch({ control });

  const earnSummary = useAtomValue(earnSummaryAtom);
  const { from: fromPrecision, to: toPrecision } = useAtomValue(precisionAtom);
  const targetCurrencyAppliedApr = getEarnSummaryOfCurrency(toCurrency ?? '', earnSummary).appliedAprPct;

  const ConvertInfo = [
    {
      id: 'from',
      title: <FormattedMessage id='Convert.r250401.From' />,
      currency: fromCurrency,
      price: <Price price={fromAmount} precision={fromPrecision} className={priceStyle} />,
    },
    {
      id: 'to',
      title: <FormattedMessage id='Deposit.r250401.To' />,
      currency: toCurrency,
      price: <Price price={toAmount} precision={toPrecision} className={priceStyle} />,
    },
  ];

  const GoToEarnButtonText = t('Convert.r250401.Earning_apr_banner_text', {
    symbol: toCurrency,
    percent: `${targetCurrencyAppliedApr.toString()}%`,
  });

  return (
    <>
      <div
        className={vstack({
          h: '100%',
          justify: 'space-between',
          textAlign: 'center',
          px: { tablet: 16 },
          '& > div': { w: '100%' },
        })}
      >
        <div>
          <header className={css({ textStyle: 'Title/S', color: 'content.accent.info', '& > *': { mb: 24 } })}>
            <CheckCircleIcon size={40} />
            <p>
              <FormattedMessage id='Convert.r250401.Conversion_complete' />
            </p>
          </header>
          <ul
            className={css({
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: 24,
              p: 24,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderColor: 'border.default.level-2',
              mx: { base: 16, tablet: 0 },
            })}
          >
            {ConvertInfo.map((ci) => (
              <li key={ci.id}>
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5', mb: 8 })}>
                  {ci.title}
                </p>
                <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5', mb: 2 })}>
                  {ci.currency}
                </p>
                {ci.price}
              </li>
            ))}
          </ul>
        </div>
        {targetCurrencyAppliedApr.gt(0) && (
          <IsomorphicLink
            href='/earn'
            className={flex({
              justify: 'center',
              align: 'center',
              gap: 8,
              w: '100%',
              p: '12px 16px',
              mt: 24,
              textAlign: 'left',
              bg: 'background.default.level-3',
              borderRadius: 8,
              '& > p': {
                textStyle: 'Text/M/Strong',
                flex: 1,
                color: 'content.default.level-5',
              },
            })}
            onClick={() => segment.track(StakeEvent.StakeGoToEarnClick, { button_text: GoToEarnButtonText })}
          >
            <div
              className={css({
                w: 32,
                h: 32,
                p: 4,
                bg: 'background.default.level-0',
                borderRadius: '50%',
              })}
            >
              <EarnIcon size={24} color='#fff' />
            </div>
            <p>{GoToEarnButtonText}</p>
            <ChevronRightIcon size={20} color='#B4B4B4' />
          </IsomorphicLink>
        )}
      </div>
      <footer>
        <Button onClick={close} label={<FormattedMessage id='Reward_hub.r250213.Done' />} size='large' />
      </footer>
    </>
  );
};

const priceStyle = css({ color: 'content.default.level-2' });
